import { useSetToast } from "@app/components/Toast";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Button } from "@design-system/Inputs/Button";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Tabs } from "@design-system/Tabs";
import { QuestionnaireStatus, ResponseStatus } from "@generated/client/graphql";
import { useState, type FC } from "react";
import { useParams } from "react-router-dom";
import { useQueryParam } from "use-query-params";
import { OpenQuestionnaireModal } from "../../../components/OpenQuestionnaireModal";
import { SendQuestionnaireEmailModal } from "../../../components/SendQuestionnaireEmailModal";
import { useSendQuestionnaireEmail } from "../../../data";
import { activeTabParamArgs, supplierTabs } from "../../ctx";
import {
    useOpenQuestionnaire,
    useSupplier,
    useSupplierQuestionnaires,
} from "../../data";
import { AddUsersModal } from "../AddUsersModal";
import { QuestionnairesTab } from "../QuestionnairesTab";
import { UserTab } from "../UserTab";
import { useSupplierBodyTranslations } from "./i18n";

export const SupplierBody: FC = () => {
    const [activeTab, setActiveTab] = useQueryParam(
        ...activeTabParamArgs.activeTab,
    );

    const [isOpenQuestionnaireModalOpen, setIsOpenQuestionnaireModalOpen] =
        useState<boolean>(false);

    const [
        isSendQuestionnaireEmailModalOpen,
        setIsSendQuestionnaireEmailModalOpen,
    ] = useState<boolean>(false);

    const { supplier_id } = useParams();

    const { supplier } = useSupplier(supplier_id);

    const { questionnaires } = useSupplierQuestionnaires(
        supplier_id,
        { status: QuestionnaireStatus.Open },
        {
            take: 1,
        },
    );

    const { openQuestionnaire } = useOpenQuestionnaire();

    const {
        sendQuestionnaireEmail,
        isPending: isSendQuestionnaireEmailPending,
    } = useSendQuestionnaireEmail();

    const { setToastSuccess, setToastError } = useSetToast();

    const lastQuestionnaire = questionnaires?.list[0];

    const { t, keys } = useSupplierBodyTranslations();

    const handleOpenQuestionnaireClick = () => {
        setIsOpenQuestionnaireModalOpen(true);
    };

    const handleOpenModalConfirmClick = (
        selectedQuestionnaireTagId: string,
        name?: string,
        deadline?: Date,
    ) => {
        if (!supplier_id) return;
        openQuestionnaire(
            {
                supplierId: supplier_id,
                questionnaireTagId: selectedQuestionnaireTagId,
                questionnaireName: name,
                deadline: deadline,
            },
            {
                onSettled(data, error) {
                    if (
                        error ||
                        data?.openQuestionnaire?.status !==
                            ResponseStatus.Success
                    ) {
                        setToastError(t(keys.open_questionnaire_error_message));
                        return;
                    }
                    setToastSuccess(t(keys.open_questionnaire_success_message));
                },
            },
        );
    };

    const handleSendReminder = () => {
        setIsSendQuestionnaireEmailModalOpen(true);
    };

    const handleSendEmailModalConfirmClick = (
        senderName: string,
        emailSubject: string,
        emailText: string,
        emailHtml: string,
        isTest: boolean,
        userIds?: string[],
    ) => {
        if (!lastQuestionnaire?.id) return;
        sendQuestionnaireEmail(
            {
                questionnaireId: lastQuestionnaire?.id,
                email: {
                    senderName: senderName,
                    subject: emailSubject,
                    text: emailText,
                    html: emailHtml,
                },
                isTest,
                userIds,
            },
            {
                onSettled(data, error) {
                    if (
                        error ||
                        data?.sendSupplierQuestionnaireEmail?.status !==
                            ResponseStatus.Success
                    ) {
                        setToastError(t(keys.send_email_error_message));
                        return;
                    }
                    setToastSuccess(t(keys.send_email_success_message));
                },
            },
        );
    };

    return (
        <FlexCol gap="4">
            <FlexRow justifyContent="between" alignItems="center">
                <Tabs
                    activeTab={activeTab}
                    onActiveTabChange={setActiveTab}
                    size="md"
                >
                    <Tabs.Trigger
                        value={supplierTabs.questionnaires}
                        label={t(keys.questionnaires)}
                    />
                    <Tabs.Trigger
                        value={supplierTabs.users}
                        label={t(keys.users)}
                    />
                </Tabs>
                {activeTab === supplierTabs.questionnaires && (
                    <FlexRow gap="3">
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <div>
                                    <Button
                                        disabled={!!lastQuestionnaire}
                                        onClick={handleOpenQuestionnaireClick}
                                    >
                                        {t(keys.send_new_questionnaire_button)}
                                    </Button>
                                </div>
                            </Tooltip.Trigger>
                            {lastQuestionnaire && (
                                <Tooltip.Content>
                                    {t(keys.send_new_questionnaire_tooltip)}
                                </Tooltip.Content>
                            )}
                        </Tooltip>
                        {lastQuestionnaire && (
                            <Button
                                onClick={handleSendReminder}
                                loading={isSendQuestionnaireEmailPending}
                            >
                                {t(keys.send_reminder_button)}
                                <Button.Icon name="bell" />
                            </Button>
                        )}
                        <OpenQuestionnaireModal
                            open={isOpenQuestionnaireModalOpen}
                            onOpenChange={setIsOpenQuestionnaireModalOpen}
                            onConfirm={handleOpenModalConfirmClick}
                        />
                        {supplier && (
                            <SendQuestionnaireEmailModal
                                open={isSendQuestionnaireEmailModalOpen}
                                onOpenChange={
                                    setIsSendQuestionnaireEmailModalOpen
                                }
                                onSendClick={handleSendEmailModalConfirmClick}
                                supplierUsers={supplier.users}
                            />
                        )}
                    </FlexRow>
                )}
                {activeTab === supplierTabs.users && (
                    <AddUsersModal supplierId={supplier_id} />
                )}
            </FlexRow>
            {activeTab === supplierTabs.questionnaires && <QuestionnairesTab />}
            {activeTab === supplierTabs.users && (
                <UserTab supplierId={supplier_id} />
            )}
        </FlexCol>
    );
};
